import React, { useMemo, useRef, useState } from 'react';
import { GraduationCap, Stethoscope, Users, CheckSquare, Loader2, Search, X, UploadCloud, FileSpreadsheet, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useFetch, Spinner } from '../lib/useFetch';
import { companiesApi, workersApi, bulkApi, importApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const SectionCard = ({ icon: Icon, color, bg, title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
    <div className="flex items-start gap-3 mb-5 flex-wrap">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: bg, color }}>
        <Icon size={20} />
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-800 leading-tight">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5 max-w-xl">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

// Reusable: worker multi-select
const WorkerMultiSelect = ({ workers, companies, value, onChange, isClient }) => {
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');

  const filtered = useMemo(() => {
    let list = workers || [];
    if (companyFilter !== 'all') list = list.filter((w) => w.companyId === companyFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((w) => `${w.firstName||''} ${w.lastName||''}`.toLowerCase().includes(q) || (w.fiscalCode || '').toLowerCase().includes(q));
    }
    return list;
  }, [workers, companyFilter, search]);

  const companyName = (id) => (companies || []).find((c) => c.id === id)?.name || '';

  const toggle = (wid) => onChange(value.includes(wid) ? value.filter((x) => x !== wid) : [...value, wid]);

  const selectAllVisible = () => onChange(Array.from(new Set([...value, ...filtered.map((w) => w.id)])));
  const clearAll = () => onChange([]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome o codice fiscale..." className="pl-9 h-10" data-testid="worker-search" />
        </div>
        {!isClient && (
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger data-testid="company-filter"><SelectValue placeholder="Tutte le aziende" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {(companies || []).filter((c) => c.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500 flex-wrap gap-2">
        <span data-testid="selected-count"><b>{value.length}</b> lavoratori selezionati · {filtered.length} visibili</span>
        <div className="flex gap-2">
          <button onClick={selectAllVisible} className="px-3 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold">Seleziona visibili</button>
          {value.length > 0 && (
            <button onClick={clearAll} className="px-3 py-1 rounded-full bg-red-50 hover:bg-red-100 text-red-700 font-semibold flex items-center gap-1"><X size={12} /> Pulisci</button>
          )}
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="worker-list">
        {filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">Nessun lavoratore trovato</div>
        ) : filtered.map((w) => (
          <label key={w.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
            <Checkbox checked={value.includes(w.id)} onCheckedChange={() => toggle(w.id)} data-testid={`worker-cb-${w.id}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800">{w.firstName} {w.lastName}</div>
              <div className="text-[11px] text-slate-500">{w.fiscalCode || '—'} · {companyName(w.companyId)}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// Bulk Formazione
// ============================================================
const FormazioneBulk = ({ workers, companies, isClient }) => {
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ corso: '', dataRilascio: '', scadenza: '', durata: '', docente: '', note: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!selected.length) return toast.error('Seleziona almeno un lavoratore');
    if (!form.corso.trim()) return toast.error('Inserisci il nome del corso');
    setSaving(true);
    try {
      const res = await bulkApi.formazione({
        workerIds: selected,
        corso: form.corso.trim(),
        dataRilascio: form.dataRilascio,
        scadenza: form.scadenza,
        durata: Number(form.durata || 0),
        docente: form.docente,
        note: form.note,
      });
      toast.success(`${res.data.created} corsi creati`);
      setSelected([]);
      setForm({ corso: '', dataRilascio: '', scadenza: '', durata: '', docente: '', note: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard icon={GraduationCap} color="#023F80" bg="#e0e7ff" title="Assegna corso di formazione a più lavoratori" subtitle="Seleziona i lavoratori coinvolti e compila i dettagli del corso. Verrà creato un record di formazione per ciascun lavoratore selezionato.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">1. Seleziona lavoratori</h3>
          <WorkerMultiSelect workers={workers} companies={companies} value={selected} onChange={setSelected} isClient={isClient} />
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">2. Dettagli del corso</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Corso *</label>
              <Input value={form.corso} onChange={(e) => set('corso', e.target.value)} placeholder="es. ANTINCENDIO RISCHIO MEDIO" data-testid="bulk-corso" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Data rilascio</label>
                <Input type="date" value={form.dataRilascio} onChange={(e) => set('dataRilascio', e.target.value)} data-testid="bulk-dataRilascio" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Scadenza</label>
                <Input type="date" value={form.scadenza} onChange={(e) => set('scadenza', e.target.value)} data-testid="bulk-scadenza" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Durata (h)</label>
                <Input type="number" min={0} value={form.durata} onChange={(e) => set('durata', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Docente / Ente</label>
                <Input value={form.docente} onChange={(e) => set('docente', e.target.value)} placeholder="opzionale" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Note</label>
              <Input value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="opzionale" />
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              Verranno creati <b>{selected.length}</b> record di formazione, uno per lavoratore.
            </div>
            <button
              onClick={submit}
              disabled={saving || !selected.length || !form.corso.trim()}
              data-testid="bulk-formazione-submit"
              className="ro-hide w-full px-4 py-3 rounded-full bg-[#023F80] hover:bg-[#1e293b] text-white font-bold text-sm flex items-center gap-2 justify-center disabled:opacity-40"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckSquare size={16} />}
              Crea {selected.length} corsi
            </button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

// ============================================================
// Bulk Visite
// ============================================================
const VisiteBulk = ({ workers, companies, isClient }) => {
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ tipo: '', dataVisita: '', scadenza: '', medico: '', idoneita: 'idoneo', note: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!selected.length) return toast.error('Seleziona almeno un lavoratore');
    if (!form.tipo.trim()) return toast.error('Inserisci il tipo di visita');
    setSaving(true);
    try {
      const res = await bulkApi.visite({ workerIds: selected, ...form });
      toast.success(`${res.data.created} visite create`);
      setSelected([]);
      setForm({ tipo: '', dataVisita: '', scadenza: '', medico: '', idoneita: 'idoneo', note: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard icon={Stethoscope} color="#15803d" bg="#dcfce7" title="Programma visite mediche per più lavoratori" subtitle="Utile per programmare le visite periodiche con il medico competente: seleziona i lavoratori e crea un record di visita per ciascuno.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">1. Seleziona lavoratori</h3>
          <WorkerMultiSelect workers={workers} companies={companies} value={selected} onChange={setSelected} isClient={isClient} />
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">2. Dettagli della visita</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo visita *</label>
              <Input value={form.tipo} onChange={(e) => set('tipo', e.target.value)} placeholder="es. Periodica annuale" data-testid="bulk-tipo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Data visita</label>
                <Input type="date" value={form.dataVisita} onChange={(e) => set('dataVisita', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Scadenza</label>
                <Input type="date" value={form.scadenza} onChange={(e) => set('scadenza', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Medico competente</label>
                <Input value={form.medico} onChange={(e) => set('medico', e.target.value)} placeholder="opzionale" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Idoneità</label>
                <Select value={form.idoneita} onValueChange={(v) => set('idoneita', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="idoneo">Idoneo</SelectItem>
                    <SelectItem value="idoneo_con_prescrizioni">Idoneo con prescrizioni</SelectItem>
                    <SelectItem value="non_idoneo">Non idoneo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Note</label>
              <Input value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="opzionale" />
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              Verranno create <b>{selected.length}</b> visite mediche, una per lavoratore.
            </div>
            <button
              onClick={submit}
              disabled={saving || !selected.length || !form.tipo.trim()}
              data-testid="bulk-visite-submit"
              className="ro-hide w-full px-4 py-3 rounded-full bg-[#15803d] hover:bg-[#14532d] text-white font-bold text-sm flex items-center gap-2 justify-center disabled:opacity-40"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckSquare size={16} />}
              Crea {selected.length} visite
            </button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

const BulkOperationsPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const { data: workers, loading: lw } = useFetch(() => workersApi.list(), []);
  const { data: companies, loading: lc } = useFetch(() => companiesApi.list(), []);

  if (lw || lc) return <Spinner />;

  return (
    <div data-testid="bulk-page">
      <Tabs defaultValue="formazione" className="w-full">
        <TabsList className="bg-slate-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="formazione" data-testid="tab-bulk-formazione" className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
            <GraduationCap size={14} /> Formazione bulk
          </TabsTrigger>
          <TabsTrigger value="visite" data-testid="tab-bulk-visite" className="data-[state=active]:bg-[#15803d] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
            <Stethoscope size={14} /> Visite mediche bulk
          </TabsTrigger>
          {!isClient && (
            <TabsTrigger value="import" data-testid="tab-bulk-import" className="data-[state=active]:bg-[#b45309] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
              <UploadCloud size={14} /> Import lavoratori Excel
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="formazione" className="mt-5">
          <FormazioneBulk workers={workers} companies={companies} isClient={isClient} />
        </TabsContent>
        <TabsContent value="visite" className="mt-5">
          <VisiteBulk workers={workers} companies={companies} isClient={isClient} />
        </TabsContent>
        {!isClient && (
          <TabsContent value="import" className="mt-5">
            <ImportWorkersTab companies={companies} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

// ============================================================
// Import lavoratori da Excel
// ============================================================
const ImportWorkersTab = ({ companies }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileInput = useRef(null);

  const companyName = (id) => (companies || []).find((c) => c.id === id)?.name || id;

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
  };

  const downloadTemplate = () => {
    // Build a minimal CSV template the user can open in Excel
    const csv = [
      'Nome,Cognome,Codice Fiscale,Mansione,Email,Telefono,companyId,Data Nascita,Data Assunzione',
      'Mario,Rossi,RSSMRA80A01H501Z,Operaio,mario.rossi@example.it,3331112222,job81-4726,1980-01-01,2024-06-01',
      'Luigi,Verdi,VRDLGI82B02H501X,Tecnico,,,job81-4726,15/02/1982,01/05/2025',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'template-import-lavoratori.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const submitPreview = async () => {
    if (!file) return toast.error('Seleziona un file');
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await importApi.preview(fd);
      setPreview(res.data);
      if (res.data.errors?.length === 0) {
        toast.success(`Tutto pronto: ${res.data.valid_rows} lavoratori validi`);
      } else {
        toast.warning(`${res.data.valid_rows} validi · ${res.data.errors.length} con errori`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview');
    } finally { setPreviewing(false); }
  };

  const submitConfirm = async () => {
    if (!preview) return;
    const valid = (preview.preview || []).filter((p) => !p.errors?.length).map((p) => p.data);
    if (!valid.length) return toast.error('Nessuna riga valida da importare');
    setConfirming(true);
    try {
      const res = await importApi.confirm(valid);
      toast.success(`${res.data.created} lavoratori importati`);
      setFile(null); setPreview(null);
      if (fileInput.current) fileInput.current.value = '';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore import');
    } finally { setConfirming(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5" data-testid="import-section">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#fef7d4] text-[#8a6608]">
          <UploadCloud size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-gray-800 leading-tight">Importa lavoratori da Excel</h2>
          <p className="text-xs text-slate-500 mt-0.5">Carica un file .xlsx con i lavoratori. Colonne supportate: Nome, Cognome, Codice Fiscale, Mansione, Email, Telefono, companyId, Data Nascita, Data Assunzione.</p>
        </div>
        <button onClick={downloadTemplate} data-testid="download-template" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700">
          <Download size={14} /> Scarica template
        </button>
      </div>

      <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center">
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.xls"
          onChange={onFile}
          data-testid="import-file-input"
          className="block mx-auto text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#023F80] file:text-white hover:file:bg-[#1e293b]"
        />
        {file && <p className="text-xs text-slate-500 mt-3">{file.name} — {(file.size / 1024).toFixed(1)} KB</p>}
        <div className="mt-4 flex gap-2 justify-center">
          <button onClick={submitPreview} disabled={!file || previewing} data-testid="import-preview-btn" className="ro-hide px-4 py-2 rounded-full bg-[#023F80] text-white text-xs font-bold flex items-center gap-2 disabled:opacity-40">
            {previewing ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
            Analizza file
          </button>
        </div>
      </div>

      {preview && (
        <div className="space-y-3" data-testid="import-preview">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold flex items-center gap-1">
              <CheckCircle2 size={13} /> {preview.valid_rows} validi
            </span>
            {preview.errors?.length > 0 && (
              <span className="px-3 py-1.5 rounded-full bg-red-100 text-red-800 text-xs font-bold flex items-center gap-1">
                <AlertCircle size={13} /> {preview.errors.length} con errori
              </span>
            )}
            <span className="text-xs text-slate-500">Totale righe: {preview.total_rows}</span>
            <button
              onClick={submitConfirm}
              disabled={confirming || !preview.valid_rows}
              data-testid="import-confirm-btn"
              className="ro-hide ml-auto px-5 py-2 rounded-full bg-[#15803d] hover:bg-[#14532d] text-white text-xs font-bold flex items-center gap-2 disabled:opacity-40"
            >
              {confirming ? <Loader2 size={14} className="animate-spin" /> : <CheckSquare size={14} />}
              Importa {preview.valid_rows} lavoratori
            </button>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-xl max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Riga</th>
                  <th className="text-left px-3 py-2">Nome Cognome</th>
                  <th className="text-left px-3 py-2">Codice Fiscale</th>
                  <th className="text-left px-3 py-2">Mansione</th>
                  <th className="text-left px-3 py-2">Azienda</th>
                  <th className="text-left px-3 py-2">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(preview.preview || []).map((p) => (
                  <tr key={p.row} className={p.errors?.length ? 'bg-red-50' : ''}>
                    <td className="px-3 py-2 text-slate-400">{p.row}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{p.data.firstName} {p.data.lastName}</td>
                    <td className="px-3 py-2 text-slate-600">{p.data.fiscalCode || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{p.data.mansione || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{p.data.companyId ? companyName(p.data.companyId) : '—'}</td>
                    <td className="px-3 py-2">
                      {p.errors?.length ? (
                        <span className="text-red-700 text-[11px]">{p.errors.join(', ')}</span>
                      ) : (
                        <span className="text-emerald-700 text-[11px] flex items-center gap-1"><CheckCircle2 size={11} /> OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkOperationsPage;
